import React, { useState, useEffect, useRef } from 'react';
import { Box, Alert, Button, CircularProgress, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { tokenService } from '../../../services/tokenService';

/**
 * Klammern den vom Iframe gemeldeten Hoehenwert auf einen sinnvollen Bereich.
 * Min 500 px wegen 16:9-Standard-Folien; Max 5000 px als Belt-and-Suspenders
 * gegen Runaway-Loops wie in #296 (Heike sah 236971 px).
 *
 * Exportiert fuer den Regression-Test — siehe H5PPlayer.test.ts.
 */
export function clampIframeHeight(raw: number): number {
    if (!Number.isFinite(raw)) return 500;
    return Math.max(500, Math.min(5000, raw));
}

// H5P via same-origin proxy (setupProxy.js in dev, reverse proxy in prod)

interface H5PPlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: any) => Promise<boolean>;
}

const H5PPlayer: React.FC<H5PPlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const [h5pCompleted, setH5pCompleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    // Issue #158: H5P-Service postet die echte Content-Höhe via h5p-resize.
    // 720 als sinnvoller Anfangs-Wert für 16:9-Folien, hochgesetzt von 500.
    const [iframeHeight, setIframeHeight] = useState(720);

    const goFullscreen = () => {
        const el = iframeRef.current;
        if (!el || typeof el.requestFullscreen !== 'function') return;
        el.requestFullscreen().catch(() => {
            // Browser kann Fullscreen ablehnen (Permissions-Policy, vom Nutzer
            // deaktiviert, kein User-Gesture). Stillschweigend ignorieren —
            // der Button bleibt klickbar fuer einen zweiten Versuch.
        });
    };

    const contentId = lesson.content?.contentId;
    // Issue #170: Token wird als Query-Param in die iframe-URL eingebaut und
    // bleibt dort fixiert. Wenn der JWT zwischenzeitlich ablaeuft, antwortet
    // der H5P-Service mit 401 und der Player zeigt "invalid". Der User
    // weiss dann nicht warum — bisher hat nur Re-Login geholfen. Pre-check
    // hier macht das sichtbar.
    const tokenValid = tokenService.hasValidToken();
    const getToken = () => localStorage.getItem('token') || '';
    const playerUrl = contentId
        ? `/h5p/${contentId}/play?token=${encodeURIComponent(getToken())}`
        : '';

    // Issue #160: Wenn der User zwischen zwei H5P-Lektionen wechselt, bleibt
    // diese Komponente gemountet, aber `lesson.id` ändert sich. Ohne expliziten
    // Reset würde der iframe alten Inhalt zeigen (Browser-iframe-src-Update
    // ist nicht immer zuverlässig), und `loading`/`h5pCompleted` würden auf
    // den Werten der vorigen Lektion bleiben. Der iframe selbst bekommt
    // unten zusätzlich ein `key` für sauberes Re-Mounting.
    useEffect(() => {
        setLoading(true);
        setH5pCompleted(false);
    }, [lesson.id]);

    // Listen for resize events from iframe (#158).
    // Issue #296: kein +20 mehr — das war der Verstaerker im Feedback-Loop,
    // weil die iframe-Hoehe pro Runde wuchs, H5P-CSS den body auf die neue
    // iframe-Hoehe streckte und der Iframe-Code dann das groessere
    // body.scrollHeight gemeldet hat. Heike sah Hoehen von 236971 px.
    // Iframe meldet jetzt die intrinsische .h5p-content-Hoehe, wir setzen
    // die iframe-Hoehe exakt darauf. Defensive Max-Cap auf 5000 px, falls
    // ein Iframe trotzdem mal eine extreme Zahl postet.
    useEffect(() => {
        const resizeHandler = (e: MessageEvent) => {
            if (e.data?.type === 'h5p-resize' && typeof e.data.height === 'number') {
                setIframeHeight(clampIframeHeight(e.data.height));
            }
        };
        window.addEventListener('message', resizeHandler);
        return () => window.removeEventListener('message', resizeHandler);
    }, []);

    // Listen for xAPI events from iframe
    useEffect(() => {
        const handler = async (e: MessageEvent) => {
            if (e.data?.type === 'h5p-xapi') {
                const statement = e.data.statement;
                if (!statement) return;
                const verb = statement?.verb?.id || '';
                if (verb.includes('completed') || verb.includes('answered')) {
                    const score = statement?.result?.score;
                    if (statement?.result?.completion || verb.includes('completed')) {
                        setH5pCompleted(true);
                        if (onProgressUpdate) {
                            await onProgressUpdate(lesson.id, {
                                status: 'completed',
                                progressPercentage: 100,
                                score: score ? `${score.raw}/${score.max}` : undefined,
                            });
                        }
                    }
                }
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [lesson.id, onProgressUpdate]);

    if (!contentId) {
        return <Alert severity="warning">Kein H5P-Inhalt für diese Lektion konfiguriert.</Alert>;
    }

    if (!tokenValid) {
        return (
            <Alert
                severity="warning"
                action={
                    <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                        Neu anmelden
                    </Button>
                }
            >
                Ihre Sitzung ist abgelaufen. Bitte melden Sie sich neu an, um den H5P-Inhalt anzuzeigen.
            </Alert>
        );
    }

    return (
        <Box>
            {lesson.description && <Alert severity="info" sx={{ mb: 2 }}>{lesson.description}</Alert>}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, minHeight: 32 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} /><Typography>H5P wird geladen...</Typography>
                    </Box>
                ) : <span />}
                <Tooltip title="Vollbild">
                    <IconButton size="small" onClick={goFullscreen} aria-label="Vollbild">
                        <FullscreenIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <iframe
                ref={iframeRef}
                key={lesson.id}
                src={playerUrl}
                style={{ width: '100%', height: iframeHeight, border: '1px solid #e0e0e0', borderRadius: 4 }}
                onLoad={() => setLoading(false)}
                allow="fullscreen"
            />

            {h5pCompleted && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>Lektion abgeschlossen!</Typography>
                        <Chip label="Bestanden" color="success" size="small" />
                    </Box>
                </Alert>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={onComplete}>
                    {h5pCompleted ? 'Bestätigen' : 'Als abgeschlossen markieren'}
                </Button>
            </Box>
        </Box>
    );
};

export default H5PPlayer;
